<template>
  <modal name="AddFontModal" :width="'80%'" :height="'90%'">
    <Card class="h-100">
      <template v-slot:header-left><h6 class="text-white mb-0">{{ dictionary.fontModal.modalTitle }}</h6></template>
      <template v-slot:header-right><button type="button" class="btn-close btn-close-white" aria-label="Close" @click="close"></button></template>
      <div v-if="!newFontMode" class="h-100 d-flex flex-column">
        <div class="flex-shrink-1">
          <div class="row d-flex align-items-center">
            <div class="col-auto">
              <a class="btn btn-outline-secondary" href="https://fonts.google.com/" target="_blank">{{ dictionary.fontModal.goToGoogleFont }}</a>
            </div>
            <div class="col-auto ms-3">
              <button @click="newFontMode = true" type="button" class="btn btn-success btn-raised text-white"><i class="fas fa-plus-circle me-2"></i>{{ dictionary.fontModal.addCustomFont }}</button>
            </div>
            <div class="col-4 ms-auto">
              <div class="form-floating">
                <input v-model="searchQuery" type="text" class="form-control" id="searchInput" placeholder="Nom de la police">
                <label for="searchInput">{{ dictionary.fontModal.searchFont }}</label>
              </div>
            </div>
          </div>
        </div>
        <div class="row flex-fill" style="overflow-y: hidden;">
            <div class="col-6 d-flex flex-column h-100">
              <div class="row flex-shrink-1">
                <div class="col-auto">
                  <h5 class="text-secondary">{{ dictionary.fontModal.googleFonts }}<i v-tooltip.top="`${dictionary.fontModal.googleFontsTooltip}`" class="ms-3 far fa-question-circle text-primary"></i></h5>
                </div>
                <div class="col">
                  <hr>
                </div>
              </div>
              <div class="flex-fill" style="overflow-y: hidden;">
                <p v-if="!loadedGoogleFont" class="text-center text-muted">{{ dictionary.fontModal.googleFontsLoading }}</p>
                <perfect-scrollbar v-else class="h-100 pe-3" ref="googleFontsScrollContainer" :options="{ suppressScrollX: true, wheelPropagation: false }">
                  <table class="table">
                    <thead>
                    <tr>
                      <th scope="col"></th>
                      <th scope="col">{{ dictionary.fontModal.fontFamily }}</th>
                      <th scope="col">{{ dictionary.fontModal.fontCategory }}</th>
                      <th scope="col">{{ dictionary.fontModal.fontVariants }}</th>
                      <th scope="col">{{ dictionary.fontModal.fontImported }}</th>
                      <th scope="col">{{ dictionary.fontModal.fontActions }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(font, key) in slicedGoogleFonts" :key="key">
                      <td><a v-tooltip="'Voir la police sur Google Fonts'" class="btn btn-info btn-raised" target="_blank" :href="`https://fonts.google.com/specimen/${font.family.replaceAll(' ', '+')}`"><i class="me-0 fas fa-eye me-2"></i><i class="fab fa-google"></i></a></td>
                      <td><h5>{{ font.family }}</h5></td>
                      <td>{{ font.category }}</td>
                      <td class="text-center" style="word-wrap: break-word;max-width:200px">
                        <span class="badge bg-secondary me-1">{{ font.variants.length }}</span>
                      </td>
                      <td class="text-center">
                        <span v-if="isFontImported(font)" class="badge bg-primary">{{ dictionary.yes }}</span>
                      </td>
                      <td class="d-flex justify-content-end h-100">
                        <button v-tooltip="'Importer la police'" v-if="!isFontImported(font)" @click="addGoogleFont(font)" class="btn btn-success btn-raised mx-1 text-light"><i class="me-0 fas fa-plus"><i class="ms-2 fas fa-chevron-right"></i></i></button>
                      </td>
                    </tr>
                    <tr>
                      <mugen-scroll scroll-container="googleFontsScrollContainer" :handler="fetchData" :should-handle="!mugenLoading">
                        <p v-if="scrolledEnd">{{ dictionary.fontModal.googleFontsEnd }}</p>
                      </mugen-scroll>
                    </tr>
                    </tbody>
                  </table>
                </perfect-scrollbar>
              </div>
            </div>
            <div class="col-6 d-flex flex-column h-100">
              <div class="row flex-shrink-1">
                <div class="col-auto">
                  <h5 class="text-secondary">{{ dictionary.fontModal.importedFonts }}</h5>
                </div>
                <div class="col">
                  <hr>
                </div>
              </div>
              <div class="flex-fill" style="overflow-y: hidden">
                <p v-if="fontListLength <= 0" class="text-muted text-center">{{ dictionary.fontModal.noFontForTheMoment }}</p>
                <perfect-scrollbar v-else class="h-100 pe-3" ref="googleFontsScrollContainer" :options="{ suppressScrollX: true, wheelPropagation: false }">
                  <table class="table">
                    <thead>
                    <tr>
                      <th scope="col">{{ dictionary.fontModal.source }}</th>
                      <th scope="col">{{ dictionary.fontModal.fontFamily }}</th>
                      <th scope="col"></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="font in Object.entries(fontList)" :key="font[0]">
                      <td>{{ font[1].source }}</td>
                      <td><h5>{{ font[1].family }}</h5></td>
                      <td colspan="1" class="d-flex justify-content-end h-100">
                        <button @click="removeFont(font[0])" class="btn btn-danger btn-raised mx-1"><i class="me-2 fas fa-chevron-left" v-if="font[1].source === 'GoogleFont'"></i><i class="me-0 fas fa-trash"></i></button>
                        <a v-if="font[1].source === 'GoogleFont'" class="btn btn-info btn-raised" target="_blank" :href="`https://fonts.google.com/specimen/${font[1].family.replaceAll(' ', '+')}`"><i class="me-0 fas fa-eye"></i></a>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </perfect-scrollbar>
              </div>
            </div>
        </div>
      </div>
      <FontFromFile v-else @cancel="newFontMode = false"/>
    </Card>
  </modal>
</template>

<script>

import MugenScroll from 'vue-mugen-scroll';
import { mapGetters } from 'vuex';
import VueNotifications from 'vue-notifications';
import Card from '../../Utilities/Card.vue';
import FontFromFile from './FontFromFile.vue';

export default {
  name: 'FontManager',
  components: {
    FontFromFile,
    Card,
    MugenScroll,
  },
  props: {
    fontList: Object,
  },
  model: {
    prop: 'fontList',
    event: 'newFont',
  },
  computed:
  {
    ...mapGetters(['dictionary']),
    fontListLength()
    {
      if (!this.fontList)
        return 0;
      return Object.keys(this.fontList).length;
    },
    slicedGoogleFonts()
    {
      // eslint-disable-next-line arrow-body-style
      const filtered = this.googleFontList.filter((font) => {
        if (this.searchQuery)
          return font.family.toLowerCase().includes(this.searchQuery.toLowerCase());
        return font;
      });
      if (filtered.length > this.displayed)
        return filtered.slice(0, this.displayed);
      return filtered;
    },
  },
  data()
  {
    return {
      searchQuery: '',
      googleFontList: [],
      loadedGoogleFont: false,
      scrolledEnd: false,
      mugenLoading: false,
      displayed: 20,
      newFontMode: false,
    };
  },
  methods: {
    fetchData()
    {
      this.scrolledEnd = false;
      this.mugenLoading = true;
      if (this.googleFontList && this.googleFontList.length > this.displayed)
        this.displayed += 10;
      else
        this.scrolledEnd = true;
      this.mugenLoading = false;
    },
    open()
    {
      this.newFontMode = false;
      this.$modal.show('AddFontModal');
      this.$store.dispatch('getGoogleFontList').then((items) => {
        this.googleFontList = items;
        this.loadedGoogleFont = true;
      });
    },
    close()
    {
      this.$modal.hide('AddFontModal');
    },
    computeGoogleFontURL(font)
    {
      let weightString = '';
      font.variants.forEach((variant) => {
        if (variant === 'regular')
          weightString += '0,400;';
        else if (variant !== 'italic' && !variant.includes('italic'))
          weightString += `0,${variant};`;
      });
      font.variants.forEach((variant) => {
        if (variant === 'italic')
          weightString += '1,400;';
        else if (variant.includes('italic'))
          weightString += `1,${variant.replace('italic', '')};`;
      });
      weightString = weightString.replace(/;+$/, '');
      return `https://fonts.googleapis.com/css2?family=${font.family.replace(' ', '+')}:ital,wght@${weightString}&display=swap`;
    },
    addGoogleFont(font)
    {
      const out = {};
      out.source = 'GoogleFont';
      out.GoogleFontURL = this.computeGoogleFontURL(font);
      out.family = font.family;
      if (font.category === 'sans-serif' || font.category === 'serif' || font.category === 'monospace')
        out.category = font.category;
      this.$store.commit('addGoogleFont', { fontObject: out, dbSync: true });
      this.fontSaveNotif();
    },
    isFontImported(font)
    {
      return !(Object.values(this.fontList).every((importedFont) => (importedFont.family !== font.family)));
    },
    removeFont(fontID)
    {
      this.$store.commit('deleteGoogleFont', { fontID, dbSync: true });
      this.fontRemoveNotif();
    },
  },
  notifications: {
    fontSaveNotif: {
      type: VueNotifications.types.success,
      title: 'Police importé',
      message: 'La police a bien été ajouté au registre !<br>Vous pouvez maintenant l\'utiliser',
    },
    fontRemoveNotif: {
      type: VueNotifications.types.success,
      title: 'Police supprimée',
      message: 'La police a bien été supprimée.',
    },
  },
};

</script>

<style scoped>

</style>
